import { useEffect, useId, useMemo, useRef, useState } from 'react';

export interface ComboOption {
  value: string;
  label: string;
  /** Extra text a person might type to find this row — an id, an engine, a team.
   *  Matched but never displayed, so pasting a uuid selects the row it belongs to. */
  keywords?: string[];
}

/**
 * Editable single-select combobox (WAI-ARIA APG "combobox with list autocomplete").
 *
 * WHY NOT A `<select>`. The list it replaces is the scanned DigitalOcean databases,
 * and the operator arrives at this form from the DO console with either a name in
 * their head or a uuid on their clipboard. A native select can be typed at only by
 * first letter, and shows nothing but the label — so a pasted uuid found nothing,
 * which is what pushed the earlier design towards a free-text uuid field.
 *
 * THE LIST IS THE SCAN AND NOTHING ELSE (owner decision, e-13). There is no synthetic
 * "use this id anyway" option: an id that is not in the scan is either a database
 * this app has no token for or a typo, and both create a target that can never
 * reconcile. Typing something unmatched leaves nothing selected and says so.
 *
 * `keywords` is what makes the paste work — the uuid, the engine and the team are
 * matched but not rendered, so the visible row stays `name · engine · team`.
 */
export function ComboBox({
  label,
  placeholder,
  options,
  value,
  onPick,
  emptyText = 'No match',
  describedBy,
  autoFocus = false,
  id: idProp,
}: {
  /** Accessible name — there is no visible <label> wrapping this input. */
  label: string;
  placeholder?: string;
  options: ComboOption[];
  /** The picked option's value, or '' for nothing picked. */
  value: string;
  onPick: (value: string) => void;
  emptyText?: string;
  describedBy?: string;
  autoFocus?: boolean;
  id?: string;
}) {
  const auto = useId();
  const id = idProp ?? auto;
  const listId = `${id}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Only a KEY press scrolls the list. `onMouseEnter` moves `active` too, and
  // scrolling under a stationary cursor moves the next row beneath it — the list
  // then walks by itself. The flag is a ref, not state: it must not re-render.
  const keyNav = useRef(false);

  const picked = options.find((o) => o.value === value) ?? null;

  // While the list is shut the input SHOWS the picked row; while it is open it shows
  // what is being typed. One value with two jobs would either erase the pick on the
  // first keystroke or refuse to be edited at all.
  const shown = open ? query : (picked?.label ?? '');

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.label, ...(o.keywords ?? [])].some((t) => t.toLowerCase().includes(q)),
    );
  }, [options, query]);

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  // Keyboard-moving `aria-activedescendant` past the bottom of a scrolled list leaves
  // the row a screen reader announces off screen for everyone else — the list is the
  // whole DO scan, so it scrolls. `nearest` because a picker should not jump the page.
  useEffect(() => {
    if (!open || !keyNav.current) return;
    keyNav.current = false;
    const row = listRef.current?.children[active];
    if (row instanceof HTMLElement) row.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  // Clicking anywhere else commits nothing and shuts the list — the pick is whatever
  // it already was, never the half-typed filter.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (opt: ComboOption) => {
    onPick(opt.value);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="combo" ref={rootRef}>
      <input
        ref={inputRef}
        id={id}
        className="class-select combo__input"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={label}
        aria-describedby={describedBy}
        aria-activedescendant={open && matches[active] ? `${id}-opt-${active}` : undefined}
        autoComplete="off"
        data-autofocus={autoFocus || undefined}
        placeholder={placeholder}
        value={shown}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          // Editing the filter un-picks: leaving a stale selection behind text that
          // no longer describes it is how a form submits the wrong database.
          if (value) onPick('');
        }}
        onFocus={() => setOpen(true)}
        /* Focus alone is not enough: after a pick the input KEEPS focus (`choose`
           refocuses it), so clicking the field the operator is already in — the
           obvious "show me the list again" gesture — fired no event that reopened
           it. ArrowDown below is the keyboard half of the same reopen. */
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            keyNav.current = true;
            if (!open) setOpen(true);
            else setActive((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            keyNav.current = true;
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            if (open && matches[active]) {
              // Only when it lands on a row: Enter on a highlighted option picks it
              // and goes no further. With nothing highlighted the press is left
              // alone — no <form> wraps this, so it does nothing at all.
              e.preventDefault();
              choose(matches[active]);
            }
          } else if (e.key === 'Escape') {
            if (open) {
              // Shut the list, keep the dialog. Escape with the list open is "never
              // mind this list", not "throw away the form".
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
            }
          }
        }}
      />
      <ul
        className="combo__list"
        id={listId}
        role="listbox"
        aria-label={label}
        hidden={!open}
        ref={listRef}
      >
        {matches.map((o, i) => (
          <li
            key={o.value}
            id={`${id}-opt-${i}`}
            role="option"
            aria-selected={o.value === value}
            className={`combo__opt${i === active ? ' combo__opt--active' : ''}`}
            /* mousedown, not click: the input's blur would shut the list first. */
            onMouseDown={(e) => {
              e.preventDefault();
              choose(o);
            }}
            onMouseEnter={() => {
              keyNav.current = false;
              setActive(i);
            }}
          >
            {o.label}
          </li>
        ))}
        {!matches.length && (
          <li className="combo__opt combo__opt--empty" role="presentation">
            {emptyText}
          </li>
        )}
      </ul>
    </div>
  );
}
